import { useParams } from "react-router-dom";
import { IIdentifiable, ReactComponentType, ReactFunctionalComponent, useIdBuilder, useWithDefaultBuilder, useWithDefaults } from "..";
import { useUserContext } from "../../_runtime/iam/session.context";
import { findFirstValueDefined, hasPropOrPath } from "../../packages/core/toolbox/objects";
import { applyActions } from "./utils.actions";
import { isUndefined } from "ramda-adjunct";
import { pipe, prop } from 'ramda';


export type WithRootieProps = {
  category?: string;
  type?: string;
  useDefaults?: boolean;
  useId?: boolean;
  useConditions?: boolean;
}

const transformPropsWithConditions = (props: any, currentContextParams: any) => {
  const actions = findFirstValueDefined(["component.actions","actions"],props,undefined);
  if(isUndefined(actions)) {
    return props;
  }
  const newProps = applyActions(props, actions,currentContextParams);
  return newProps;
}

const useTransformPropsWithDefaults = (category: string, type: string, props: any) => {
  return useWithDefaults(category, prop(type, props), { ...props });
};

const createTransformationPipe = (rootieProps: WithRootieProps, contextParams: any,mergedPropsBuilder:any) => {
  const transformations: Array<(props: any) => any> = [];

  if (rootieProps.useDefaults) {
    transformations.push((props: any) =>
      mergedPropsBuilder(props)
    );
  }

  if (rootieProps.useConditions) {
    transformations.push((props: any) => transformPropsWithConditions(props, contextParams));
  }

  return (pipe as any)(...transformations);
};
export const withRootie = <P extends IIdentifiable>(
  InnerComponent: ReactComponentType<any>,
  rootieProps: WithRootieProps = {}
): ReactFunctionalComponent<any> => {
  const { useId = true} = rootieProps;
  return ({ id:componentId, ...props }: any) => {
    const id = useId ? useIdBuilder(componentId) : componentId;
    const urlParams = useParams();
    const userContext = useUserContext();
    const contextParams = { ...urlParams, ...userContext };
    const mergedPropsBuilder = useWithDefaultBuilder(rootieProps.category!, props.kind??props.action??props.id!);


    // Debug log for current context parameters

    const transformProps = createTransformationPipe(rootieProps, contextParams, mergedPropsBuilder);

    if (hasPropOrPath('component.props')(props)) {
      const { component: { props: compProps, ...restComponent }, ...restProps } = props;
      const newProps = {
        ...restProps,
        component: {
          props: transformProps( compProps),
          ...restComponent,
        },
      };
      return <InnerComponent {...(newProps as P)} id={id} />;
    }

    if (hasPropOrPath('props')(props)) {
      const { props: compProps, ...restProps } = props;
      const newProps = {
        ...restProps,
        props: transformProps(compProps),
      };
      return <InnerComponent {...(newProps as P)} id={id} />;
    }
    const newProps = transformProps(mergedPropsBuilder(props));

    return <InnerComponent {...(newProps as P)} id={id} />;
  };
};
